/** 
 * @license @magicbell/react-headless v4.2.4
 *
 * Copyright (c) MagicBell Inc. and its affiliates.
 *
 * This source code is licensed under the license found in the
 * LICENSE file in the root directory of this source tree.
 */

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target2 = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target2[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target2[prop] = source[prop];
    }
  return target2;
};
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import warning from "tiny-warning";
import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { useEffect, useState, useCallback } from "react";
import create from "zustand/vanilla";
import create$1 from "zustand";
import humps from "humps";
import axios from "axios";
import produce from "immer";
import { mergeRight, uniqBy, isNil, findIndex, propEq, path } from "ramda";
import * as Ably from "ably";
import mitt from "mitt";
import isEmpty from "lodash/isEmpty";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import relativeTime from "dayjs/plugin/relativeTime";
import updateLocale from "dayjs/plugin/updateLocale";
const clientSettings = create(() => ({
  apiKey: "",
  apiSecret: void 0,
  userEmail: void 0,
  userExternalId: void 0,
  userKey: void 0,
  clientId: Math.random().toString(36).substring(2) + Date.now(),
  serverURL: "https://api.magicbell.com"
}));
const ns = "__MAGICBELL_HOOKS__";
const target = typeof window === "undefined" ? global : window;
const registry = {
  get(key, fallback) {
    return target[ns] && target[ns][key] ? target[ns][key] : fallback;
  },
  set(key, value) {
    target[ns] = target[ns] || {};
    target[ns][key] = value;
  }
};
function buildAPIHeaders() {
  const { apiKey, apiSecret, clientId, userEmail, userExternalId, userKey } = clientSettings.getState();
  const headers = {
    "X-MAGICBELL-CLIENT-ID": clientId,
    "X-MAGICBELL-API-KEY": apiKey,
    "X-MagicBell-Client": "ReactHeadless/4.0.0"
  };
  if (apiSecret)
    headers["X-MAGICBELL-API-SECRET"] = apiSecret;
  if (userEmail)
    headers["X-MAGICBELL-USER-EMAIL"] = userEmail;
  if (userKey)
    headers["X-MAGICBELL-USER-HMAC"] = userKey;
  if (userExternalId)
    headers["X-MAGICBELL-USER-EXTERNAL-ID"] = userExternalId;
  return headers;
}
function sendAPIRequest(method, url, data, params, headers = {}) {
  const { serverURL } = clientSettings.getState();
  const requestHeaders = __spreadValues(__spreadValues({}, buildAPIHeaders()), headers);
  const client = registry.get("axios", axios);
  return client({
    method,
    url,
    data,
    params,
    headers: requestHeaders,
    baseURL: serverURL
  }).then(
    (response) => response.data,
    (error) => {
      throw error;
    }
  );
}
function fetchAPI(url, params = {}, headers) {
  return sendAPIRequest("get", url, void 0, params, headers);
}
function postAPI(url, data = {}, params = {}, headers) {
  return sendAPIRequest("post", url, data, params, headers);
}
function deleteAPI(url, params = {}, headers) {
  return sendAPIRequest("delete", url, void 0, params, headers);
}
function putAPI(url, data, params = {}, headers) {
  return sendAPIRequest("put", url, data, params, headers);
}
class ConfigRepository {
  constructor(remotePathOrUrl = "/config") {
    __publicField(this, "remotePathOrUrl");
    this.remotePathOrUrl = remotePathOrUrl;
  }
  async get() {
    const json = await fetchAPI(this.remotePathOrUrl);
    return humps.camelizeKeys(json);
  }
}
const useConfig = create$1((set, get) => ({
  channels: void 0,
  inbox: void 0,
  ws: void 0,
  lastFetchedAt: void 0,
  _repository: new ConfigRepository(),
  fetch: async () => {
    const { _repository } = get();
    const json = await _repository.get();
    set(__spreadProps(__spreadValues({}, json), { lastFetchedAt: Date.now() }));
  }
}));
class RemoteRepository {
  constructor(remotePathOrUrl) {
    __publicField(this, "remotePathOrUrl");
    this.remotePathOrUrl = remotePathOrUrl;
  }
  async get(id) {
    const url = `${this.remotePathOrUrl}/${id}`;
    const json = await fetchAPI(url);
    return humps.camelizeKeys(json);
  }
  async findBy(queryParams) {
    try {
      const json = await fetchAPI(this.remotePathOrUrl, queryParams);
      return humps.camelizeKeys(json);
    } catch (error) {
      if (/Network Error/.test(error.message)) {
        return;
      }
      throw error;
    }
  }
  delete(id) {
    const url = `${this.remotePathOrUrl}/${id}`;
    return deleteAPI(url).then(() => true).catch(() => false);
  }
}
class NotificationRepository extends RemoteRepository {
  constructor(remotePathOrUrl = "/notifications") {
    super(remotePathOrUrl);
  }
  markAsRead(id) {
    const url = `${this.remotePathOrUrl}/${id}/read`;
    return postAPI(url).then(() => true).catch(() => false);
  }
  markAsUnread(id) {
    const url = `${this.remotePathOrUrl}/${id}/unread`;
    return postAPI(url).then(() => true).catch(() => false);
  }
  markAllAsSeen(params) {
    const url = `${this.remotePathOrUrl}/seen`;
    return postAPI(url, void 0, params).then(() => true).catch(() => false);
  }
  markAllAsRead(params) {
    const url = `${this.remotePathOrUrl}/read`;
    return postAPI(url, void 0, params).then(() => true).catch(() => false);
  }
}
const pushEventAggregator = mitt();
const eventAggregator = mitt();
function connectToAbly(config) {
  const { serverURL } = clientSettings.getState();
  const authUrl = `${serverURL}/${config.authUrl}`;
  const authHeaders = buildAPIHeaders();
  const client = new Ably.Realtime({
    authUrl,
    authHeaders,
    authMethod: "POST",
    log: { level: 0 },
    transports: ["web_socket"]
  });
  return client;
}
function emitEvent(event, data, source) {
  if (source === "remote") {
    pushEventAggregator.emit(event, data);
  }
  eventAggregator.emit(event, { data, source });
}
function handleAblyEvent(event) {
  const { clientId } = clientSettings.getState();
  const eventName = event.name.replace(/\//gi, ".");
  const eventData = event.data;
  const isLoopbackEvent = eventData.client_id && eventData.client_id === clientId;
  if (isLoopbackEvent)
    return Promise.resolve(false);
  if (typeof eventData.id === "string") {
    if (eventName === "notifications.delete") {
      emitEvent(eventName, eventData, "remote");
      return Promise.resolve(true);
    } else {
      const repository = new NotificationRepository();
      return repository.get(eventData.id).then((data) => {
        emitEvent(eventName, data.notification, "remote");
        return true;
      });
    }
  }
  emitEvent(eventName, eventData, "remote");
  return Promise.resolve(true);
}
function buildStore(props) {
  const defaults = {
    context: {},
    total: 0,
    totalPages: 0,
    perPage: 0,
    currentPage: 1,
    unreadCount: 0,
    unseenCount: 0,
    notifications: []
  };
  return mergeRight(defaults, props);
}
function setStoreProps(store, props, options = { reset: false }) {
  const _a = props, { notifications } = _a, meta = __objRest(_a, ["notifications"]);
  const allNotifications = options.reset ? notifications : uniqBy(
    (notification) => notification.id,
    options.prepend ? [...notifications, ...store.notifications] : [...store.notifications, ...notifications]
  );
  return __spreadValues({ context: store.context, notifications: allNotifications }, meta);
}
function eq(value, other) {
  return value === other || value !== value && other !== other;
}
function ensureArray(value) {
  return Array.isArray(value) ? value : String(value).split(",");
}
function objMatchesContext(notification, context, comparator = eq) {
  const diff = [];
  Object.keys(context).forEach((attr) => {
    const condition = context[attr];
    if (attr === "read" && !comparator(!isNil(notification.readAt), condition) || attr === "seen" && !comparator(!isNil(notification.seenAt), condition) || attr === "categories" && ensureArray(condition).every((category) => !comparator(notification.category, category)) || attr === "topics" && ensureArray(condition).every((topic) => !comparator(notification.topic, topic)) || Object.hasOwnProperty.call(notification, attr) && !comparator(notification[attr], condition)) {
      diff.push(attr);
    }
  });
  return { result: diff.length === 0, delta: diff };
}
const useNotificationStoresCollection = create$1((set, get) => ({
  stores: {},
  _repository: new NotificationRepository(),
  setStore: (storeId, defaultQueryParams = {}, otherProps = {}) => {
    set(
      produce((draft) => {
        draft.stores[storeId] = buildStore(__spreadProps(__spreadValues({}, otherProps), { context: defaultQueryParams }));
      })
    );
  },
  fetchStore: async (storeId, queryParams = {}, options = {}) => {
    const { stores, _repository } = get();
    const store = stores[storeId];
    if (store) {
      const response = await _repository.findBy(__spreadValues(__spreadValues({}, store.context), queryParams));
      if (!response)
        return;
      set(
        produce((draft) => {
          draft.stores[storeId] = setStoreProps(store, __spreadProps(__spreadValues({}, response), { lastFetchedAt: new Date() }), options);
        })
      );
    } else {
      console.error(`Store not found. Define a store with the ${storeId} ID`);
    }
  },
  fetchAllStores: async (queryParams = {}, options = {}) => {
    const { stores, fetchStore } = get();
    const storeIds = Object.keys(stores);
    const fetchers = storeIds.map((storeId) => fetchStore(storeId, queryParams, options));
    await Promise.all(fetchers);
  },
  markNotificationAsSeen: (notification) => {
    const { stores } = get();
    const notificationId = notification.id;
    emitEvent("notifications.seen", notification, "local");
    set(
      produce((draft) => {
        for (const storeId in stores) {
          const { notifications, unseenCount } = stores[storeId];
          const index = findIndex(propEq("id", notificationId), notifications);
          if (index > -1) {
            const notification2 = notifications[index];
            if (!notification2.seenAt) {
              draft.stores[storeId].unseenCount = Math.max(0, unseenCount - 1);
              draft.stores[storeId].notifications[index] = mergeRight(notifications[index], {
                seenAt: Date.now() / 1e3
              });
            }
          }
        }
      })
    );
  },
  markNotificationAsRead: (notification) => {
    const { stores, _repository } = get();
    const { id: notificationId } = notification;
    const promise = _repository.markAsRead(notificationId);
    emitEvent("notifications.read", notification, "local");
    set(
      produce((draft) => {
        const now = Date.now() / 1e3;
        const attrs = { readAt: now, seenAt: now };
        for (const storeId in stores) {
          const { total, notifications, context, unreadCount, unseenCount } = stores[storeId];
          const index = findIndex(propEq("id", notificationId), notifications);
          if (index > -1) {
            if (!notification.readAt)
              draft.stores[storeId].unreadCount = Math.max(0, unreadCount - 1);
            if (!notification.seenAt)
              draft.stores[storeId].unseenCount = Math.max(0, unseenCount - 1);
            const readNotification = mergeRight(notifications[index], attrs);
            if (objMatchesContext(readNotification, context).result) {
              draft.stores[storeId].notifications[index] = readNotification;
            } else {
              draft.stores[storeId].total = Math.max(0, total - 1);
              draft.stores[storeId].notifications.splice(index, 1);
            }
          } else {
            const readNotification = mergeRight(notification, attrs);
            if (objMatchesContext(readNotification, context).result) {
              draft.stores[storeId].total += 1;
              draft.stores[storeId].notifications.push(readNotification);
            }
          }
        }
      })
    );
    return promise;
  },
  markNotificationAsUnread: (notification) => {
    const { stores, _repository } = get();
    const { id: notificationId } = notification;
    const promise = _repository.markAsUnread(notificationId);
    emitEvent("notifications.unread", notification, "local");
    set(
      produce((draft) => {
        const attrs = { readAt: null };
        for (const storeId in stores) {
          const { notifications, context } = stores[storeId];
          const index = findIndex(propEq("id", notificationId), notifications);
          if (index > -1) {
            const unreadNotification = mergeRight(notifications[index], attrs);
            if (objMatchesContext(unreadNotification, context).result) {
              if (notification.readAt)
                draft.stores[storeId].unreadCount += 1;
              draft.stores[storeId].notifications[index] = unreadNotification;
            } else {
              draft.stores[storeId].total = Math.max(0, draft.stores[storeId].total - 1);
              draft.stores[storeId].notifications.splice(index, 1);
            }
          } else {
            const unreadNotification = mergeRight(notification, attrs);
            if (objMatchesContext(unreadNotification, context).result) {
              draft.stores[storeId].total += 1;
              if (notification.readAt)
                draft.stores[storeId].unreadCount += 1;
              draft.stores[storeId].notifications.push(unreadNotification);
            }
          }
        }
      })
    );
    return promise;
  },
  deleteNotification: (notification, options = {}) => {
    const { stores, _repository } = get();
    const notificationId = notification.id;
    let promise = Promise.resolve(true);
    if (options.persist !== false) {
      promise = _repository.delete(notificationId);
      emitEvent("notifications.delete", notification, "local");
    }
    set(
      produce((draft) => {
        for (const storeId in stores) {
          const { notifications, total, unseenCount, unreadCount } = stores[storeId];
          const index = findIndex(propEq("id", notificationId), notifications);
          if (index > -1) {
            const notification2 = notifications[index];
            if (!notification2.seenAt)
              draft.stores[storeId].unseenCount = Math.max(0, unseenCount - 1);
            if (!notification2.readAt)
              draft.stores[storeId].unreadCount = Math.max(0, unreadCount - 1);
            draft.stores[storeId].total = Math.max(0, total - 1);
            draft.stores[storeId].notifications.splice(index, 1);
          }
        }
      })
    );
    return promise;
  },
  markAllAsSeen: (options = { persist: true, updateModels: true }) => {
    var _a;
    const { stores, _repository } = get();
    let promise = Promise.resolve(true);
    if (options.persist !== false) {
      const params = options.storeId ? (_a = stores[options.storeId]) == null ? void 0 : _a.context : {};
      promise = _repository.markAllAsSeen(params);
      emitEvent("notifications.seen.all", null, "local");
    }
    set(
      produce((draft) => {
        const changedNotifications = /* @__PURE__ */ new Map();
        const now = Date.now() / 1e3;
        for (const storeId in stores) {
          const { context } = stores[storeId];
          draft.stores[storeId].unseenCount = 0;
          if (options.updateModels === false)
            continue;
          if (context.seen !== true) {
            for (const notification of draft.stores[storeId].notifications) {
              if (notification.seenAt)
                continue;
              notification.seenAt = now;
              changedNotifications.set(notification.id, notification);
            }
          }
          if (context.seen === false) {
            draft.stores[storeId].notifications = [];
            draft.stores[storeId].total = 0;
          }
        }
        for (const storeId in stores) {
          const { context } = stores[storeId];
          if (context.seen !== true)
            continue;
          const notifications = draft.stores[storeId].notifications;
          for (const notification of changedNotifications.values()) {
            if (objMatchesContext(notification, context).result && !notifications.find((n) => n.id === notification.id)) {
              notifications.push(notification);
              draft.stores[storeId].total += 1;
            }
          }
        }
      })
    );
    return promise;
  },
  markAllAsRead: (options = { persist: true, updateModels: true }) => {
    var _a;
    const { stores, _repository } = get();
    let promise = Promise.resolve(true);
    if (options.persist !== false) {
      const params = options.storeId ? (_a = stores[options.storeId]) == null ? void 0 : _a.context : {};
      promise = _repository.markAllAsRead(params);
      emitEvent("notifications.read.all", null, "local");
    }
    set(
      produce((draft) => {
        const changedNotifications = /* @__PURE__ */ new Map();
        const now = Date.now() / 1e3;
        for (const storeId in stores) {
          const { context } = stores[storeId];
          draft.stores[storeId].unreadCount = 0;
          draft.stores[storeId].unseenCount = 0;
          if (options.updateModels === false)
            continue;
          if (context.read !== true) {
            for (const notification of draft.stores[storeId].notifications) {
              if (notification.readAt)
                continue;
              notification.readAt = now;
              notification.seenAt = now;
              changedNotifications.set(notification.id, notification);
            }
          }
          if (context.read === false) {
            draft.stores[storeId].notifications = [];
            draft.stores[storeId].total = 0;
          }
        }
        for (const storeId in stores) {
          const { context } = stores[storeId];
          if (context.read !== true)
            continue;
          const notifications = draft.stores[storeId].notifications;
          for (const notification of changedNotifications.values()) {
            if (objMatchesContext(notification, context).result && !notifications.find((n) => n.id === notification.id)) {
              notifications.push(notification);
              draft.stores[storeId].total += 1;
            }
          }
        }
      })
    );
    return promise;
  }
}));
function createRealtimeSubscription(config) {
  let recoveringConnection = false;
  const { channel: realtimeChannel } = config;
  const ablyClient = connectToAbly(config);
  ablyClient.connection.on("suspended", (stateChange) => {
    if (stateChange.previous === "disconnected")
      emitEvent("disconnected", stateChange, "local");
  });
  ablyClient.connection.on((stateChange) => {
    const { current, previous } = stateChange;
    if (["suspended", "disconnected"].includes(previous) && current === "connecting") {
      recoveringConnection = true;
    }
    if (recoveringConnection && current === "connected") {
      recoveringConnection = false;
      emitEvent("reconnected", stateChange, "local");
    }
  });
  const ablyChannel = ablyClient.channels.get(realtimeChannel);
  ablyChannel.subscribe(handleAblyEvent);
  return () => {
    ablyChannel.unsubscribe(handleAblyEvent);
    ablyChannel.detach();
    ablyClient.connection.off();
    ablyClient.close();
  };
}
function useAbly() {
  const config = useConfig();
  useEffect(() => {
    if (config.ws)
      return createRealtimeSubscription(config.ws);
    else
      return () => void 0;
  }, [config.ws]);
}
function useMagicBellEvent(event, handler, options = { source: "any" }) {
  useEffect(() => {
    const callback = (args = {}) => {
      if (options.source === "remote" && args.source !== "remote")
        return;
      if (options.source === "local" && args.source !== "local")
        return;
      handler(args.data, args.source);
    };
    eventAggregator.on(event, callback);
    return () => {
      eventAggregator.off(event, callback);
    };
  }, []);
}
function RealtimeListener() {
  const collection = useNotificationStoresCollection();
  const fetchAndResetAll = () => collection.fetchAllStores({ page: 1 }, { reset: true });
  const fetchAndPrependAll = () => collection.fetchAllStores({ page: 1 }, { prepend: true });
  const markAllAsSeen = () => collection.markAllAsSeen({ persist: false });
  const markAllAsRead = () => collection.markAllAsRead({ persist: false });
  const removeNotification = (data) => collection.deleteNotification(data, { persist: false });
  useAbly();
  useMagicBellEvent("reconnected", fetchAndResetAll);
  useMagicBellEvent("notifications.new", fetchAndPrependAll, { source: "remote" });
  useMagicBellEvent("notifications.seen.all", markAllAsSeen, { source: "remote" });
  useMagicBellEvent("notifications.read.all", markAllAsRead, { source: "remote" });
  useMagicBellEvent("notifications.read", fetchAndResetAll, { source: "remote" });
  useMagicBellEvent("notifications.unread", fetchAndResetAll, { source: "remote" });
  useMagicBellEvent("notifications.delete", removeNotification, { source: "remote" });
  return null;
}
function setupXHR(_a) {
  var _b = _a, { serverURL } = _b, userSettings = __objRest(_b, ["serverURL"]);
  const settings = userSettings;
  if (serverURL)
    settings.serverURL = serverURL;
  clientSettings.setState(settings);
  return settings;
}
function setupStores(storesConfig) {
  const stores = {};
  storesConfig.forEach((store) => {
    const { defaultQueryParams: context, defaults = {} } = store;
    stores[store.id] = buildStore(__spreadValues({ context }, defaults));
  });
  useNotificationStoresCollection.setState({ stores });
  return stores;
}
function MagicBellProvider(_c) {
  var _d = _c, {
    children,
    stores = [{ id: "default", defaultQueryParams: {} }],
    disableRealtime
  } = _d, clientSettings2 = __objRest(_d, [
    "children",
    "stores",
    "disableRealtime"
  ]);
  useState(() => setupXHR(clientSettings2));
  useEffect(() => {
    setupStores(stores);
  }, []);
  const config = useConfig();
  useEffect(() => {
    if (!config.lastFetchedAt)
      config.fetch();
  }, [config]);
  return /* @__PURE__ */ jsxs(Fragment, {
    children: [
      disableRealtime ? null : /* @__PURE__ */ jsx(RealtimeListener, {}),
      children
    ]
  });
}
function stringToUint8Array(plainString) {
  const padding = "=".repeat((4 - plainString.length % 4) % 4);
  const base64 = (plainString + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
function storeSubscription(subscription) {
  const { userEmail, userExternalId, userKey, apiKey } = clientSettings.getState();
  const headers = { "X-MAGICBELL-API-KEY": apiKey };
  if (!isEmpty(userEmail))
    headers["X-MAGICBELL-USER-EMAIL"] = userEmail;
  if (!isEmpty(userExternalId))
    headers["X-MAGICBELL-USER-EXTERNAL-ID"] = userExternalId;
  if (!isEmpty(userKey))
    headers["X-MAGICBELL-USER-HMAC"] = userKey;
  const data = {
    web_push_subscription: {
      data: subscription
    }
  };
  return postAPI("/web_push_subscriptions", data, { headers });
}
function subscribeToPushNotifications(pushManager, publicKey) {
  const applicationServerKey = stringToUint8Array(publicKey);
  return pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });
}
async function createPushSubscription(pushManager, config) {
  const vapidPublicKey = path(["webPush", "config", "vapidAuthentication", "publicKey"], config.channels);
  const subscription = await subscribeToPushNotifications(pushManager, vapidPublicKey);
  return storeSubscription(subscription.toJSON());
}
function createSafariPushSubscription(authenticationToken, webServiceUrl, websitePushID = "web.com.magicbell-notifications") {
  const permissionData = window["safari"].pushNotification.permission(websitePushID);
  if (permissionData.permission === "granted")
    return Promise.resolve(permissionData);
  if (permissionData.permission === "denied")
    return Promise.reject(false);
  return new Promise(function(resolve, reject) {
    window["safari"].pushNotification.requestPermission(
      webServiceUrl,
      websitePushID,
      { authenticationToken },
      function(permissionData2) {
        if (permissionData2.deviceToken) {
          const subscriptionData = {
            endpoint: permissionData2.deviceToken,
            keys: { websitePushID },
            platform: "safari"
          };
          storeSubscription(subscriptionData).then((subscription) => {
            resolve(subscription);
          }).catch((error) => {
            reject(error);
          });
        } else {
          const error = new Error("Permission was denied");
          reject(error);
        }
      }
    );
  });
}
function WebPushNotificationsSubscriber({
  children,
  serviceWorkerPath = "/service-worker.js",
  skipServiceWorkerRegistration = false
}) {
  const config = useConfig();
  const isSafari = "safari" in window;
  const isPushAPISupported = "PushManager" in window;
  useEffect(() => {
    if (!skipServiceWorkerRegistration) {
      navigator.serviceWorker.register(serviceWorkerPath);
    }
  }, [serviceWorkerPath, skipServiceWorkerRegistration]);
  const createSubscription = async () => {
    var _a, _b, _c;
    if (!config) {
      return Promise.reject(new Error("Context for MagicBell was not found"));
    }
    if (isSafari) {
      const authenticationToken = path(["safari", "authenticationToken"], (_a = config.channels) == null ? void 0 : _a.webPush.config);
      const websitePushID = path(["safari", "websitePushId"], (_b = config.channels) == null ? void 0 : _b.webPush.config);
      const webServiceUrl = path(["safari", "webServiceUrl"], (_c = config.channels) == null ? void 0 : _c.webPush.config);
      return createSafariPushSubscription(authenticationToken, webServiceUrl, websitePushID);
    }
    if (isPushAPISupported) {
      return navigator.serviceWorker.ready.then(async (registration) => {
        await createPushSubscription(registration.pushManager, config);
      });
    }
  };
  return children({ createSubscription, isPushAPISupported });
}
function useNotifications(storeId = "default") {
  const { stores, fetchStore, markAllAsSeen, markAllAsRead } = useNotificationStoresCollection();
  const config = useConfig();
  const store = stores[storeId];
  const fetch = useCallback(
    (queryParams, options) => fetchStore(storeId, queryParams, options),
    [fetchStore, storeId]
  );
  const fetchNextPage = useCallback(
    (queryParams = {}, options) => {
      const page = store.currentPage + 1;
      return fetchStore(storeId, __spreadProps(__spreadValues({}, queryParams), { page }), options);
    },
    [fetchStore, storeId, store == null ? void 0 : store.currentPage]
  );
  useEffect(() => {
    if (!store)
      return;
    if (config.lastFetchedAt && !store.lastFetchedAt)
      fetch({ page: 1 });
  }, [config.lastFetchedAt, store, fetch]);
  const markAllAsReadFn = useCallback(
    (options) => markAllAsRead(__spreadProps(__spreadValues({}, options), { storeId })),
    [markAllAsRead, storeId]
  );
  const markAllAsSeenFn = useCallback(
    (options) => markAllAsSeen(__spreadProps(__spreadValues({}, options), { storeId })),
    [markAllAsSeen, storeId]
  );
  if (!store)
    return null;
  return __spreadProps(__spreadValues({}, store), {
    isEmpty: store.notifications.length === 0,
    hasNextPage: store.currentPage < store.totalPages,
    fetch,
    fetchNextPage,
    markAllAsSeen: markAllAsSeenFn,
    markAllAsRead: markAllAsReadFn
  });
}
function useBell({ storeId } = {}) {
  const store = useNotifications(storeId);
  const markAllAsSeen = () => {
    if (store && store.unseenCount > 0)
      return store == null ? void 0 : store.markAllAsSeen({ updateModels: false });
    return Promise.resolve(true);
  };
  if (store)
    return __spreadProps(__spreadValues({}, store), { markAllAsSeen });
  return null;
}
dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);
dayjs.extend(updateLocale);
function secondsToDate(seconds) {
  return seconds ? toDate(seconds * 1e3) : null;
}
function toDate(date) {
  return dayjs(date);
}
function toUnix(date) {
  return dayjs(date).unix();
}
function parseJSON(obj) {
  if (isNil(obj))
    return null;
  if (typeof obj === "string") {
    try {
      return JSON.parse(obj);
    } catch (e) {
      console.warn('"customAttributes" is not valid JSON');
    }
  }
  return obj;
}
function useNotificationFactory(data) {
  const { markNotificationAsRead, markNotificationAsSeen, markNotificationAsUnread, deleteNotification } = useNotificationStoresCollection();
  const markAsSeen = () => markNotificationAsSeen(data);
  const markAsRead = () => markNotificationAsRead(data);
  const markAsUnread = () => markNotificationAsUnread(data);
  const deleteSelf = () => deleteNotification(data);
  return __spreadProps(__spreadValues({}, data), {
    customAttributes: parseJSON(data.customAttributes),
    readAt: secondsToDate(data.readAt),
    seenAt: secondsToDate(data.seenAt),
    sentAt: secondsToDate(data.sentAt),
    archivedAt: secondsToDate(data.archivedAt),
    isSeen: !isNil(data.seenAt),
    isRead: !isNil(data.readAt),
    isArchived: !isNil(data.archivedAt),
    sanitizedContent: data.content,
    markAsSeen,
    markAsRead,
    markAsUnread,
    delete: deleteSelf
  });
}
function useNotificationUnmount(notification, fn) {
  useEffect(() => {
    return () => {
      if (fn)
        fn(notification);
      else
        notification.markAsSeen();
    };
  }, []);
}
function useNotification(data, onUnmount) {
  const notification = useNotificationFactory(data);
  useNotificationUnmount(notification, onUnmount);
  return notification;
}
class NotificationPreferencesRepository {
  constructor(remotePathOrUrl = "/notification_preferences") {
    __publicField(this, "remotePathOrUrl");
    this.remotePathOrUrl = remotePathOrUrl;
  }
  async get() {
    const url = this.remotePathOrUrl;
    const json = await fetchAPI(url, void 0, { "Accept-Version": "v2" });
    return humps.camelizeKeys(json);
  }
  async update(data) {
    const url = this.remotePathOrUrl;
    const payload = humps.decamelizeKeys({ notificationPreferences: data });
    const json = await putAPI(url, payload, void 0, { "Accept-Version": "v2" });
    return humps.camelizeKeys(json);
  }
}
const useNotificationPreferences = create$1((set, get) => ({
  categories: [],
  _repository: new NotificationPreferencesRepository(),
  fetch: async () => {
    const { _repository } = get();
    try {
      const { notificationPreferences: json } = await _repository.get();
      set(__spreadProps(__spreadValues({}, json), { lastFetchedAt: Date.now() }));
    } catch (error) {
      set({ categories: [], lastFetchedAt: Date.now() });
    }
  },
  save: async (preferences) => {
    const { _repository } = get();
    try {
      const { notificationPreferences: json } = await _repository.update(preferences);
      set(__spreadProps(__spreadValues({}, json), { lastFetchedAt: Date.now() }));
    } catch (error) {
      set({ categories: [], lastFetchedAt: Date.now() });
    }
  }
}));
{
  const testFunc = function testFn() {
  };
  warning(
    (testFunc.name || testFunc.toString()).indexOf("testFn") !== -1,
    `It looks like you're using a minified copy of the development build of ${"@magicbell/react-headless"}. When deploying your app to production, make sure to use the production build which is faster and does not print development warnings.`
  );
}
export {
  MagicBellProvider,
  RealtimeListener,
  WebPushNotificationsSubscriber,
  buildStore,
  clientSettings,
  deleteAPI,
  eventAggregator,
  fetchAPI,
  postAPI,
  pushEventAggregator,
  putAPI,
  registry,
  secondsToDate,
  toDate,
  toUnix,
  useBell,
  useConfig,
  useMagicBellEvent,
  useNotification,
  useNotificationFactory,
  useNotificationPreferences,
  useNotificationStoresCollection,
  useNotificationUnmount,
  useNotifications
};
//# sourceMappingURL=magicbell-react-headless.esm.js.map
